/**
 * Reverse-geocode a lat/lng pair into a short, human-readable place name.
 * Uses OpenStreetMap Nominatim (free, no API key, rate-limited 1 req/sec).
 * Returns a string like "Sandton, Johannesburg" or null on failure.
 */

let lastCallAt = 0;
const MIN_INTERVAL_MS = 1100; // Nominatim policy: max 1 req/sec

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

export const reverseGeocode = async (lat, lng) => {
  if (lat == null || lng == null) return null;

  // Throttle to respect Nominatim's rate limit
  const sinceLast = Date.now() - lastCallAt;
  if (sinceLast < MIN_INTERVAL_MS) {
    await delay(MIN_INTERVAL_MS - sinceLast);
  }
  lastCallAt = Date.now();

  try {
    const url =
      `https://nominatim.openstreetmap.org/reverse` +
      `?format=json&lat=${lat}&lon=${lng}&zoom=14&addressdetails=1`;

    const res = await fetch(url, {
      headers: { 'Accept-Language': 'en' },
    });

    if (!res.ok) return null;
    const data = await res.json();
    const a = data.address || {};

    // Try to build a short, friendly label
    const suburb =
      a.suburb ||
      a.neighbourhood ||
      a.city_district ||
      a.town ||
      a.village ||
      a.hamlet;

    const city =
      a.city ||
      a.town ||
      a.municipality ||
      a.county ||
      a.state;

    if (suburb && city && suburb !== city) return `${suburb}, ${city}`;
    if (city) return city;
    if (suburb) return suburb;

    // Fallback: first two parts of the full address
    const parts = (data.display_name || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (parts.length >= 2) return `${parts[0]}, ${parts[1]}`;
    return parts[0] || null;
  } catch (err) {
    console.warn('[geocode] reverse failed:', err?.message);
    return null;
  }
};

/**
 * Hook-friendly cache. Keeps a small in-memory map so repeated calls for
 * the same coords don't hammer Nominatim.
 */
const cache = new Map();
const cacheKey = (lat, lng) =>
  `${lat.toFixed(3)},${lng.toFixed(3)}`; // ~100m precision

export const reverseGeocodeCached = async (lat, lng) => {
  if (lat == null || lng == null) return null;
  const key = cacheKey(lat, lng);
  if (cache.has(key)) return cache.get(key);
  const label = await reverseGeocode(lat, lng);
  cache.set(key, label);
  return label;
};